import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ContentService } from '../content/content.service';
import { TranslateService } from '../translate/translate.service';
import { AuthService } from '../auth/auth.service';
import { IntervalService } from '../interval/interval.service';
import { NavigationItemService } from '../navigation-item/navigation-item.service';
import { ConnectionService } from '../connection/connection.service';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MessengerService implements OnDestroy {
  public $initialized = new BehaviorSubject<boolean>(false);
  public $languageReady = new BehaviorSubject<boolean>(false);
  public $metaPackage = new BehaviorSubject<any>(null);
  public $ticker = new BehaviorSubject<any>(null);
  public $centerWidgetScreensavers = new BehaviorSubject<any[]>(null);
  public $navigationScreensavers = new BehaviorSubject<any[]>(null);
  public $currentLanguage = new BehaviorSubject<any>(window['masterLanguage'] || 'de');
  public $changeLanguageFromMenu = new BehaviorSubject<any>(null);
  public $serverError = new BehaviorSubject<boolean>(false);
  public $checksum = new BehaviorSubject<string>(null);
  public $filter = new BehaviorSubject<any>(null);
  public $filterCounter = new BehaviorSubject<any>(null);
  public $filterIsSingleSelect = new BehaviorSubject<any>(false);
  public $selectedFilters = new BehaviorSubject<any>(null);
  public $showNavigation = new BehaviorSubject<any>(true);
  public $selectedPartner = new BehaviorSubject<any>(null);
  public $updateNavigation = new BehaviorSubject<any>(null);
  public $initialItemId = new BehaviorSubject<any>(null);
  public $currentSearchValue = new BehaviorSubject<string>('');
  public $forceBlur = new BehaviorSubject<boolean>(false);
  public $initAnalytics = new BehaviorSubject<any>(null);
  public $resetInfoscreenIndexes = new BehaviorSubject<number>(null);
  public $recommendations = new BehaviorSubject<any[]>(null);

  public currentLanguage = window['masterLanguage'] || 'de';
  public lastShownScreensaverEntry = -1;
  public mainPackage: any = null;
  public appState: any;
  private subscriptions = [];
  private languageOrder = ['de', 'en', 'it', 'fr'];
  public masterLanguage = window['masterLanguage'] || 'de';
  public availableLanguages = (window['forceAvailableLanguages'] ? window['forceAvailableLanguages'].split(',') : ['de']).sort((a, b) => {
    return this.languageOrder.indexOf(a) - this.languageOrder.indexOf(b);
  });

  constructor(
    private contentService: ContentService, private translateService: TranslateService, private auth: AuthService,
    private navService: NavigationItemService, private connection: ConnectionService
  ) {
    if (this.masterLanguage !== 'de') {
      const [item] = this.languageOrder.splice(this.languageOrder.indexOf(this.masterLanguage), 1);
      this.languageOrder.unshift(item);
      this.availableLanguages = (window['forceAvailableLanguages'] ? window['forceAvailableLanguages'].split(',') : ['de']).sort((a, b) => {
        return this.languageOrder.indexOf(a) - this.languageOrder.indexOf(b);
      });
    }
    this.initMetaPackage();
    this.initChecksum().then(() => {
    });

    this.initSession();

    this.subscriptions.push(this.navService.$hasRecommendation.pipe(filter(Boolean)).subscribe(res => {
      this.requestRecommendations(res);
    }));

    this.subscriptions.push(this.$currentLanguage.subscribe(lang => {
      this.initLanguageContent(lang).then(() => {
        IntervalService.set('messenger', () => {
          return this.contentService.loadPackageLog().then(packageLog => {
            if (this.$checksum.getValue() !== (packageLog.checksum2 || packageLog.checksum)) {
              this.$checksum.next((packageLog.checksum2 || packageLog.checksum));
              this.initLanguageContent(lang, true);
            }
          }).catch(err => {
            console.warn(err);
          });
        }, 1000 * 60);
      });
    }));
  }

  ngOnDestroy() {
    for (let subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions.length = 0;
  }

  private static initTicker(tickerElement: any, target: BehaviorSubject<any>) {
    target.next(tickerElement);
  }

  public updateLanguage(lang) {
    this.$currentLanguage.next(lang);
    this.translateService.loadTranslation(lang).then(() => {});
  }

  public async changeLanguage(lang) {
    await this.translateService.loadTranslation(lang);
    let jsonPackage = await this.contentService.loadPackage(lang, this.availableLanguages);
    if (jsonPackage) {
      this.$ticker.next(null);
      if (jsonPackage?.entries?.entryCustomerInformation?.detail?.translations) {
        jsonPackage = JSON.parse(JSON.stringify(jsonPackage).replace(/%t:([^%]+)%/ig, (_, key) => jsonPackage?.entries?.entryCustomerInformation?.detail?.translations[key]));
      }
      if (JSON.stringify(this.mainPackage) === JSON.stringify(jsonPackage)) {
        return;
      }
      this.mainPackage = JSON.parse(JSON.stringify(jsonPackage));
      this.$initAnalytics.next(Date.now());
      if (Object.keys(jsonPackage?.ticker || {}).length) {
        MessengerService.initTicker(jsonPackage.ticker, this.$ticker);
      }
      if (!!jsonPackage?.screensaver) {
        let entry = this.contentService.getEntry(jsonPackage.screensaver.entry);
        this.$centerWidgetScreensavers.next(entry.related.entries.map(entry => {
          return this.contentService.getEntry(entry.id)?.detail?.isSlideShow ? this.contentService.getEntry(entry.id) : null;
        }).filter(Boolean));
        this.$navigationScreensavers.next(entry.related.entries.map(entry => {
          return this.contentService.getEntry(entry.id)?.detail?.isNavigationSlideshow ? this.contentService.getEntry(entry.id) : null;
        }).filter(Boolean));
      }
      await this.navService.initMenu(jsonPackage.menuItems, true, true);
      this.currentLanguage = lang;
    }
  }

  public resetFilter() {
    this.$filter.next(null);
    this.$filterCounter.next(null);
    this.$filterIsSingleSelect.next(false);
    this.$selectedFilters.next(null);
  }

  private initSession() {
    /* get socket connection - socket not used currently */
    this.auth.initSession();
    this.subscriptions.push(this.auth.$activeSession.subscribe(session => {
      this.$initialized.next(true);
      if (!session) {
        return;
      }
    }));
  }

  private async initMetaPackage() {
    /* $metaPackage holds basic information about the package like title, logo ... */
    try {
      const jsonMeta = await this.contentService.initMeta();
      if (jsonMeta) {
        this.$metaPackage.next(jsonMeta);
      } else {
        setTimeout(() => {
          this.initMetaPackage()
        }, 60000);
      }
    } catch (err) {
      console.error('init meta package error:', err);
      this.$serverError.next(true);
      setTimeout(() => {
        this.initMetaPackage()
      }, 60000);
    }
  }

  private async initChecksum() {
    try {
      const packageLog = await this.contentService.loadPackageLog();
      this.$checksum.next((packageLog.checksum2 || packageLog.checksum));
    } catch (err) {
      this.$serverError.next(true);
    }
  }

  private async initLanguageContent(lang: string, force = false) {
    this.$ticker.next(null);
    /* get $mainPackage by language */
    await this.translateService.loadTranslation(lang);
    this.$languageReady.next(true);
    let jsonPackage = await this.contentService.loadPackage(lang, this.availableLanguages, force);
    if (jsonPackage) {
      if (jsonPackage && jsonPackage.entries && jsonPackage.entries.entryCustomerInformation && jsonPackage.entries.entryCustomerInformation.detail && jsonPackage.entries.entryCustomerInformation.detail.translations) {
        let json = JSON.stringify(jsonPackage);
        json = json.replace(/%t:([^%]+)%/ig, function(match, transKey) {
          return jsonPackage.entries.entryCustomerInformation.detail.translations[transKey];
        });
        jsonPackage = JSON.parse(json);
      }
      if (JSON.stringify(this.mainPackage) === JSON.stringify(jsonPackage)) {
        return;
      }
      this.mainPackage = JSON.parse(JSON.stringify(jsonPackage));
      this.$initAnalytics.next(Date.now());
      this.navService.initMenu(jsonPackage.menuItems, true).then(() => {});
      if (jsonPackage.ticker && Object.keys(jsonPackage.ticker).length) {
        MessengerService.initTicker(jsonPackage.ticker, this.$ticker);
      }
      if (!!jsonPackage.screensaver) {
        let entry = this.contentService.getEntry(jsonPackage.screensaver.entry);
        let centerWidgetScreensavers = [];
        let navigationScreensavers = [];
        for (let related of (entry?.related?.entries || [])) {
          let screensaverEntry = this.contentService.getEntry(related.id);
          if (screensaverEntry && screensaverEntry.detail && screensaverEntry.detail.isSlideShow) {
            centerWidgetScreensavers.push(screensaverEntry);
          }
          if (screensaverEntry && screensaverEntry.detail && screensaverEntry.detail.isNavigationSlideshow) {
            navigationScreensavers.push(screensaverEntry);
          }
        }
        this.$centerWidgetScreensavers.next(centerWidgetScreensavers);
        this.$navigationScreensavers.next(navigationScreensavers);
      }
    }
    // todo no connection to the server - should we get it from cache?
  }

  public async requestRecommendations(config) {
    try {
      const snippet = await this.connection.post('/recommendation/', { id: config._id });
      const recommendationData = await this.connection.getSimple(config.detail.contentUrls?.[this.currentLanguage] || config.detail.contentUrl);
      const func = new Function('return ' + snippet.code)();
      const recommendations = func.call(this, this.navService.navigationItems, recommendationData);
      this.$recommendations.next(recommendations);
      setTimeout(() => this.requestRecommendations(config), 30 * 60 * 1000);
    } catch (err) {
      console.warn(err);
    }
  }
}
